/**
 * VulnerabilityList Component
 *
 * Display list of vulnerabilities with filtering and sorting
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertTriangle, ExternalLink, Shield, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Vulnerability, SecuritySeverity } from '@forgenetwork/types';

interface VulnerabilityListProps {
  vulnerabilities: Vulnerability[];
  onFilter?: (severity: SecuritySeverity | 'all') => void;
}

/**
 * Severity badge configurations
 */
const severityConfig: Record<
  SecuritySeverity,
  { variant: 'default' | 'secondary' | 'destructive'; className: string }
> = {
  critical: {
    variant: 'destructive',
    className: 'bg-red-600 text-white hover:bg-red-700',
  },
  high: {
    variant: 'destructive',
    className: 'bg-orange-600 text-white hover:bg-orange-700',
  },
  medium: {
    variant: 'default',
    className: 'bg-yellow-600 text-white hover:bg-yellow-700',
  },
  low: {
    variant: 'secondary',
    className: 'bg-blue-600 text-white hover:bg-blue-700',
  },
  info: {
    variant: 'secondary',
    className: 'bg-gray-600 text-white hover:bg-gray-700',
  },
};

/**
 * Severity sort order (higher = more severe)
 */
const severityOrder: Record<SecuritySeverity, number> = {
  critical: 5,
  high: 4,
  medium: 3,
  low: 2,
  info: 1,
};

/**
 * Format CVE ID as NVD link
 */
function getCVEUrl(cveId?: string): string | null {
  if (!cveId) return null;
  return `https://nvd.nist.gov/vuln/detail/${cveId}`;
}

/**
 * VulnerabilityList - Display and filter vulnerabilities
 */
export function VulnerabilityList({
  vulnerabilities,
  onFilter,
}: VulnerabilityListProps): React.JSX.Element {
  const [selectedSeverity, setSelectedSeverity] = React.useState<
    SecuritySeverity | 'all'
  >('all');
  const [sortBy, setSortBy] = React.useState<'severity' | 'title'>('severity');

  // Filter vulnerabilities by severity
  const filteredVulnerabilities = React.useMemo(() => {
    return selectedSeverity === 'all'
      ? vulnerabilities
      : vulnerabilities.filter((v) => v.severity === selectedSeverity);
  }, [vulnerabilities, selectedSeverity]);

  // Sort vulnerabilities
  const sortedVulnerabilities = React.useMemo(() => {
    const sorted = [...filteredVulnerabilities];
    if (sortBy === 'severity') {
      sorted.sort(
        (a, b) => severityOrder[b.severity] - severityOrder[a.severity]
      );
    } else {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    }
    return sorted;
  }, [filteredVulnerabilities, sortBy]);

  // Handle severity filter change
  const handleSeverityChange = (value: string) => {
    const severity = value as SecuritySeverity | 'all';
    setSelectedSeverity(severity);
    onFilter?.(severity);
  };

  // Count by severity
  const severityCounts = React.useMemo(() => {
    return vulnerabilities.reduce(
      (acc, v) => {
        acc[v.severity] = (acc[v.severity] || 0) + 1;
        return acc;
      },
      {} as Record<SecuritySeverity, number>
    );
  }, [vulnerabilities]);

  if (vulnerabilities.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Shield className="h-12 w-12 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold mb-1">No Vulnerabilities Found</h3>
            <p className="text-sm text-muted-foreground">
              Your repository has no known security vulnerabilities
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Vulnerabilities ({vulnerabilities.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Severity Filter */}
            <Select value={selectedSeverity} onValueChange={handleSeverityChange}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All ({vulnerabilities.length})
                </SelectItem>
                <SelectItem value="critical">
                  Critical ({severityCounts.critical || 0})
                </SelectItem>
                <SelectItem value="high">
                  High ({severityCounts.high || 0})
                </SelectItem>
                <SelectItem value="medium">
                  Medium ({severityCounts.medium || 0})
                </SelectItem>
                <SelectItem value="low">
                  Low ({severityCounts.low || 0})
                </SelectItem>
                <SelectItem value="info">
                  Info ({severityCounts.info || 0})
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'severity' | 'title')}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="severity">By Severity</SelectItem>
                <SelectItem value="title">By Title</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" className="space-y-2">
          {sortedVulnerabilities.map((vuln) => {
            const config = severityConfig[vuln.severity];
            const cveUrl = getCVEUrl(vuln.cveId);

            return (
              <AccordionItem
                key={vuln.id}
                value={vuln.id}
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-start justify-between gap-4 w-full pr-4">
                    <div className="flex items-start gap-3 flex-1 text-left">
                      <AlertTriangle
                        className={cn(
                          'h-5 w-5 mt-0.5 shrink-0',
                          vuln.severity === 'critical' && 'text-red-600',
                          vuln.severity === 'high' && 'text-orange-600',
                          vuln.severity === 'medium' && 'text-yellow-600',
                          vuln.severity === 'low' && 'text-blue-600',
                          vuln.severity === 'info' && 'text-gray-600'
                        )}
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm leading-tight">
                          {vuln.title}
                        </h4>
                        {vuln.cveId && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {vuln.cveId}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className={cn('shrink-0', config.className)}>
                      {vuln.severity.toUpperCase()}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-2">
                  <div className="space-y-3 text-sm">
                    {/* Description */}
                    <div>
                      <h5 className="font-medium mb-1">Description</h5>
                      <p className="text-muted-foreground">{vuln.description}</p>
                    </div>

                    {/* Fixed Version */}
                    {vuln.fixedIn && (
                      <div>
                        <h5 className="font-medium mb-1">Fixed In</h5>
                        <Badge variant="outline" className="font-mono">
                          {vuln.fixedIn}
                        </Badge>
                      </div>
                    )}

                    {/* References */}
                    {vuln.references && vuln.references.length > 0 && (
                      <div>
                        <h5 className="font-medium mb-1">References</h5>
                        <div className="space-y-1">
                          {vuln.references.map((ref, idx) => (
                            <a
                              key={idx}
                              href={ref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline text-xs"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {ref}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CVE Link */}
                    {cveUrl && (
                      <div>
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="gap-2"
                        >
                          <a
                            href={cveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View in NVD Database
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {sortedVulnerabilities.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No vulnerabilities match the selected filter
          </div>
        )}
      </CardContent>
    </Card>
  );
}
